import { Submission, SubmissionMedia } from '../model/Challenges.types';
import {
  typography,
  row,
  ListCell,
  icon,
} from '../../promotions/view/components/ListCell';
import { F0, F1, TypedOmit } from '@client/new/helpers/types';
import { _noop } from '@client/new/helpers/general';
import { faMessage } from '@fortawesome/pro-regular-svg-icons';
import { ContentBase, ContentRow } from '../view/components/ScrollableContent';
import {
  formatDateOnly,
  formatDateWithTime,
} from '../../promotions/view-model/utils';
import { MediaVM } from '../view/components/common/Media';
import { getUserTZ } from './utils';

export type SubmissionCardVM = {
  id: string;
  beforeMedia: MediaVM;
  afterMedia: MediaVM;
  showInstagram: boolean;
  title: string;
  descLine: ListCell;
  isFav: boolean;
  isFavLoading: boolean;
  onClick: F0;
};

const pluralize = (v: string, n: number) => (n === 1 ? v : `${v}s`);

const toMediaVM = (urls: SubmissionMedia[], emptyText: string): MediaVM =>
  urls.length
    ? { type: urls[0].type, value: urls[0].thumbnailURL }
    : { type: 'none', value: emptyText };

const toDescLine = (s: Submission): ListCell => {
  const photosCount = [...s.beforeMedia, ...s.afterMedia].filter(
    (m) => m.type === 'photo',
  ).length;
  const videosCount = s.beforeMedia.length + s.afterMedia.length - photosCount;
  const counters: Array<string> = [];
  const cells: ListCell[] = [];
  if (photosCount)
    counters.push(`${photosCount} ${pluralize('photo', photosCount)}`);
  if (videosCount)
    counters.push(`${videosCount} ${pluralize('video', photosCount)}`);
  if (counters.length)
    cells.push(
      typography({
        content: counters.join(', '),
        variant: 'body',
        fontWeight: 'normal',
      }),
    );
  if (s.testimonial) cells.push(icon({ src: faMessage }));
  return row(cells);
};

export const toSubmissionCardVM = (
  s: Submission,
  onCLick?: F1<string>,
): SubmissionCardVM => ({
  id: s.submissionId,
  beforeMedia: toMediaVM(s.beforeMedia, 'No before media'),
  afterMedia: toMediaVM(s.afterMedia, 'No after media'),
  showInstagram: !!s.instagram,
  title: s.username,
  descLine: toDescLine(s),
  isFav: s.isFav,
  isFavLoading: false,
  onClick: () => (onCLick ? onCLick(s.submissionId) : _noop),
});
export type SubmissionCardMode = 'Before/after' | 'Transformation';

export type SubmissionVM = {
  title: string;
  submissionId: string;
  isFav: boolean;
  isFavLoading: boolean;
  metaLine: ContentRow;
  beforeMedia: SubmissionMedia[];
  beforeMediaTitle: ContentRow;
  afterMedia: SubmissionMedia[];
  afterMediaTitle: ContentRow;
  desc: ContentRow[];
  transformationMedia: SubmissionMedia[];
  showDownload: boolean;

  mode: SubmissionCardMode;
  changeMode: F1<SubmissionCardMode>;
};

const toDate = (d?: Date) =>
  d ? formatDateWithTime(d, '', getUserTZ()) : 'N/A';

const text = (value: string): ContentBase => ({ type: 'text', value });
const bold = (value: string): ContentBase => ({ type: 'bold', value });
const link = (content: string, href: string): ContentBase => ({
  type: 'link',
  value: { content, href },
});

const textRow = (name: string, value: string) => [bold(name), text(value)];

const toStatusDate = (s: Submission): string => {
  if (!s.statusDate) return '';
  const d = formatDateOnly(new Date(s.statusDate));
  switch (s.userStatus) {
    case 'Active':
      return ': Renews on ' + d;
    case 'Cancelled':
      return ': Expires on ' + d;
    case 'Inactive':
      return ': Expired on ' + d;
    default:
      return '';
  }
};

const toMetaLine = (s: Submission): ContentRow => {
  const res: ContentBase[] = [];
  if (s.instagram)
    res.push(link('Instagram', `https://www.instagram.com/${s.instagram}`));
  res.push(link('Email', `mailto:${s.email}`));
  if (s.phoneNumber) res.push(link(s.phoneNumber, `tel:${s.phoneNumber}`));
  res.push(text(s.repostable ? 'Repostable' : 'Not Repostable'));
  s.userStatus && res.push(text(s.userStatus + toStatusDate(s)));
  if (s.workoutsTotal)
    res.push(
      text(`Workouts completed: ${s.workoutsCompleted}/${s.workoutsTotal} `),
    );

  return res;
};

const toDesc = (s: Submission): ContentRow[] => {
  const res: ContentRow[] = [];
  if (s.goal) res.push(textRow('Goals:', s.goal));
  if (s.testimonial) res.push(textRow('Testimonials:', s.testimonial));

  return res;
};

export const toSubmissionVM = (
  s: Submission,
): TypedOmit<SubmissionVM, 'mode' | 'changeMode'> => {
  const showDownload =
    s.afterMedia?.length > 0 ||
    s.beforeMedia?.length > 0 ||
    s.transformationMedia?.length > 0;
  return {
    title: s.username,
    isFav: s.isFav,
    metaLine: toMetaLine(s),
    beforeMedia: s.beforeMedia,
    beforeMediaTitle: [bold('BEFORE:'), text(toDate(s.beforeDate))],
    afterMedia: s.afterMedia,
    afterMediaTitle: [bold('AFTER:'), text(toDate(s.afterDate))],
    transformationMedia: s.transformationMedia,

    desc: toDesc(s),

    isFavLoading: false,
    submissionId: s.submissionId,

    showDownload,
  };
};
